import type { HTMLAttributes, ReactNode, Ref } from 'react'
import { cx, isFilled, PortalThemeContext } from '@/shared/lib'
import { Surface } from '@/shared/ui/Surface'
import s from './AuthShell.module.css'

export type AuthShellProps = {
  /**
   * Единственный `h1` страницы. Обязателен и обязателен именно пропом: каркас гарантирует,
   * что заголовок на экране ровно один, а страница восстановления пароля и страница блокировки
   * передают свой текст — заголовок называет страницу, а не рекламирует продукт.
   */
  heading: string
  /** Абзац под заголовком. Мера строки ограничена, длинный текст переносится. */
  description?: string
  /** Знак и словесная марка. Слот: логотип — не деталь каркаса, тот же довод, что у `AppTopBar`. */
  brand?: ReactNode
  /** Ряд капсул «что внутри». Материал капсулы каркас даёт прямым детям слота, текст — потребитель. */
  highlights?: ReactNode
  /** Подвал: телефон поддержки, политика конфиденциальности. Едет в ориентир `contentinfo`. */
  meta?: ReactNode
  /**
   * Содержимое стеклянной карточки: форма входа, ввод одноразового кода, восстановление пароля,
   * «доступ приостановлен». Каркас о них не знает и начинать заголовки обязан с `h2`.
   */
  children: ReactNode
  /** Ведёт на корень кадра. */
  ref?: Ref<HTMLDivElement>
} & Omit<HTMLAttributes<HTMLDivElement>, 'children'>

/**
 * Каркас экрана, на который пользователь попадает до того, как у него появилась сессия: фон,
 * бренд-колонка и стеклянная карточка со слотом под форму. Единственная поверхность системы
 * вне `AppShell` — здесь нет шапки, навигации, прав и данных.
 *
 * Экран тёмный всегда, независимо от темы приложения: корень несёт `data-theme="dark"`, и всё
 * поддерево — `Input`, `Button`, `Alert` внутри карточки — читает уже сведённую тёмную палитру
 * проекта. Локальными остаются только значения самой поверхности входа: фон, два блика и узор.
 *
 * Корень содержимое не обрезает: `overflow: hidden` живёт на слое фона. Иначе высокая форма
 * (почта, пароль, код, чекбокс, ошибка, SSO) и экранная клавиатура телефона отрезали бы низ
 * карточки без единого способа до него добраться.
 *
 * Контракт: docs/ui-kit/contracts/auth-shell.md
 */
export function AuthShell({
  heading,
  description,
  brand,
  highlights,
  meta,
  children,
  className,
  ref,
  ...rest
}: AuthShellProps) {
  // Пустым считается и boolean: идиома `can('auth.sso') && <SsoRow/>` из правила 8 отдаёт false
  const hasBrand = isFilled(brand)
  const hasDescription = isFilled(description)
  const hasHighlights = isFilled(highlights)
  const hasMeta = isFilled(meta)

  return (
    /*
     * Порталы (выпадашки, подсказки, окна) монтируются в body и темы корня не видят:
     * контекст передаёт им тёмную тему экрана, иначе список категорий в светлой теме
     * приложения открылся бы светлым поверх тёмного экрана.
     */
    <PortalThemeContext value="dark">
      <div {...rest} ref={ref} className={cx(s.root, className)} data-theme="dark">
        {/*
         * Слой фона: блики, узор дуг, коническое кольцо и стеклянные плиты. Шесть декоративных
         * узлов не должны ни озвучиваться, ни ловить нажатия. Обрезает только он — не корень.
         */}
        <div className={s.backdrop} aria-hidden="true">
          <span className={s.glowPrimary} />
          <span className={s.glowSecondary} />
          <span className={s.slabs} />
        </div>

        {/* <header> вне секционирующего содержимого — это ориентир banner: марка и название продукта */}
        <header className={s.brand}>
          {hasBrand ? <div className={s.mark}>{brand}</div> : null}

          <div className={s.text}>
            <h1 className={s.heading}>{heading}</h1>
            {hasDescription ? <p className={s.description}>{description}</p> : null}
          </div>

          {hasHighlights ? <div className={s.highlights}>{highlights}</div> : null}
        </header>

        <main className={s.main}>
          <Surface
            className={s.card}
            variant="panel"
            radius="panel"
            elevation="panel"
            // Отступ живёт на .body: под ним колонка с щелью, а блики карточки лежат по её краю
            padding="none"
          >
            <div className={s.body}>{children}</div>
          </Surface>
        </main>

        {/*
         * contentinfo вынесен за <main> намеренно: ARIA in HTML не рекомендует его внутри main,
         * и скринридеры трактуют такой случай по-разному.
         */}
        {hasMeta ? <footer className={s.meta}>{meta}</footer> : null}
      </div>
    </PortalThemeContext>
  )
}
